import { useState, useEffect, useRef } from 'react';
import { getStats, getStudyRecommendations } from '../lib/api';
import { SUBJECTS } from '../lib/utils';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { BarChart3, Target, CheckCircle, Loader2, Sparkles, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Cell
} from 'recharts';

const StatsPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [studyAnalysis, setStudyAnalysis] = useState(null);
  const [studyLoading, setStudyLoading] = useState(false);
  const [showWrongQuestions, setShowWrongQuestions] = useState(false);
  const studySectionRef = useRef(null);

  const scrollToStudySection = () => {
    window.setTimeout(() => {
      if (!studySectionRef.current) return;

      const top = studySectionRef.current.getBoundingClientRect().top + window.scrollY - 16;
      window.scrollTo({ top, behavior: 'smooth' });
    }, 50);
  };

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    if (studyAnalysis) {
      scrollToStudySection();
    }
  }, [studyAnalysis]);

  const loadStats = async () => {
    try {
      const response = await getStats();
      setStats(response.data);
    } catch (err) {
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  const getChartData = () => {
    if (!stats?.by_subject) return [];
    
    return Object.entries(stats.by_subject).map(([key, value]) => ({
      name: value.name.split(' ')[0], // First word only for chart
      fullName: value.name,
      percentage: value.percentage,
      attempts: value.attempts,
    }));
  };

  const getBarColor = (percentage) => {
    if (percentage >= 70) return '#10B981';
    if (percentage >= 50) return '#F97316';
    return '#EF4444';
  };

  const loadStudyRecommendations = async (forceRefresh = false) => {
    setStudyLoading(true);
    scrollToStudySection();
    try {
      const response = await getStudyRecommendations(forceRefresh);
      setStudyAnalysis(response.data);
      setShowWrongQuestions(false);
      if (!response.data.cached) {
        toast.success('Análise gerada pelo Agente V.O.A.R.');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Erro ao gerar recomendações de estudo';
      toast.error(message);
    } finally {
      setStudyLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-zinc-900 text-white p-3 rounded-lg shadow-xl">
          <p className="font-medium mb-1">{data.fullName}</p>
          <p className="text-sm text-zinc-300">
            Aproveitamento: <span className="font-bold">{data.percentage}%</span>
          </p>
          <p className="text-sm text-zinc-300">
            Simulados: <span className="font-bold">{data.attempts}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto animate-fadeIn">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-zinc-900 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-zinc-900">Estatísticas</h1>
              <p className="text-zinc-500">Acompanhe sua evolução</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
          </div>
        ) : stats?.total_attempts === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="w-8 h-8 text-zinc-400" />
            </div>
            <h3 className="text-lg font-medium text-zinc-900 mb-2">
              Sem estatísticas ainda
            </h3>
            <p className="text-zinc-500">
              Complete alguns simulados para ver suas estatísticas.
            </p>
          </div>
        ) : (
          <>
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="stat-card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-zinc-100 rounded-xl flex items-center justify-center">
                    <Target className="w-6 h-6 text-zinc-600" />
                  </div>
                  <div>
                    <p className="stat-value">{stats.total_attempts}</p>
                    <p className="stat-label">Simulados</p>
                  </div>
                </div>
              </div>

              <div className="stat-card">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    stats.overall_percentage >= 70 ? 'bg-emerald-100' : 
                    stats.overall_percentage >= 50 ? 'bg-orange-100' : 'bg-red-100'
                  }`}>
                    <CheckCircle className={`w-6 h-6 ${
                      stats.overall_percentage >= 70 ? 'text-emerald-600' : 
                      stats.overall_percentage >= 50 ? 'text-orange-600' : 'text-red-600'
                    }`} />
                  </div>
                  <div>
                    <p className={`stat-value ${
                      stats.overall_percentage >= 70 ? 'text-emerald-600' : 
                      stats.overall_percentage >= 50 ? 'text-orange-600' : 'text-red-600'
                    }`}>
                      {stats.overall_percentage.toFixed(1)}%
                    </p>
                    <p className="stat-label">Aproveitamento Geral</p>
                  </div>
                </div>
              </div>

              <div className="stat-card">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-sky-600" />
                  </div>
                  <div>
                    <p className="stat-value">{Object.keys(stats.by_subject).length}</p>
                    <p className="stat-label">Matérias Estudadas</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Chart */}
            <div className="bg-white border border-zinc-200 rounded-xl p-6 mb-8">
              <h2 className="text-lg font-bold text-zinc-900 mb-6">
                Desempenho por Matéria
              </h2>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={getChartData()} layout="vertical">
                    <CartesianGrid strokeDasharray="4" stroke="#E4E4E7" />
                    <XAxis type="number" domain={[0, 100]} tick={{ fill: '#71717A' }} />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      width={100}
                      tick={{ fill: '#71717A', fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="percentage" radius={[0, 4, 4, 0]}>
                      {getChartData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getBarColor(entry.percentage)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Subject Details */}
            <h2 className="text-lg font-bold text-zinc-900 mb-4">
              Detalhes por Matéria
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(stats.by_subject).map(([key, value]) => (
                <div key={key} className="bg-white border border-zinc-200 rounded-xl p-6">
                  <h3 className="font-bold text-zinc-900 mb-4">{value.name}</h3>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-2xl font-bold text-zinc-900">{value.attempts}</p>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Simulados</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-zinc-900">
                        {value.correct_answers}/{value.total_questions}
                      </p>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Acertos</p>
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${
                        value.percentage >= 70 ? 'text-emerald-600' : 
                        value.percentage >= 50 ? 'text-orange-600' : 'text-red-600'
                      }`}>
                        {value.percentage}%
                      </p>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Média</p>
                    </div>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="mt-4">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{ 
                          width: `${value.percentage}%`,
                          backgroundColor: getBarColor(value.percentage)
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* AI Study Recommendations */}
            <div ref={studySectionRef} className="bg-white border border-zinc-200 rounded-xl p-5 sm:p-6 mt-8 scroll-mt-20">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-zinc-900 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-zinc-900">
                        Plano de estudo sugerido
                      </h2>
                      <p className="text-sm text-zinc-500">
                        O Agente V.O.A.R. analisa seu desempenho e sugere assuntos para revisar.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={() => loadStudyRecommendations(false)}
                    disabled={studyLoading}
                    className="bg-zinc-900 hover:bg-zinc-800 text-white"
                  >
                    {studyLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    Analisar meus erros
                  </Button>
                  {studyAnalysis && (
                    <Button
                      onClick={() => loadStudyRecommendations(true)}
                      disabled={studyLoading}
                      variant="outline"
                    >
                      Atualizar análise
                    </Button>
                  )}
                </div>
              </div>

              {studyLoading && (
                <div className="mt-6 rounded-lg border border-zinc-200 bg-zinc-50 p-4 flex items-center gap-3 text-zinc-600">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>O Agente V.O.A.R. está analisando seu desempenho...</span>
                </div>
              )}

              {studyAnalysis && (
                <div className="mt-6 space-y-5">
                  <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4">
                    <p className="font-semibold text-zinc-900 mb-1">Resumo</p>
                    <p className="text-zinc-700 whitespace-pre-wrap">{studyAnalysis.summary}</p>
                    <p className="text-xs text-zinc-500 mt-3">
                      Questões consideradas: {studyAnalysis.wrong_question_count}
                      {studyAnalysis.cached ? ' - análise carregada do cache' : ''}
                    </p>
                  </div>

                  {studyAnalysis.recommendations?.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {studyAnalysis.recommendations.map((item, index) => (
                        <div key={`${item.subject}-${index}`} className="rounded-lg border border-zinc-200 p-4">
                          <h3 className="font-bold text-zinc-900 mb-2">{item.subject}</h3>
                          {item.focus_topics?.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {item.focus_topics.map((topic) => (
                                <span
                                  key={topic}
                                  className="rounded-full bg-sky-50 border border-sky-200 px-2.5 py-1 text-xs font-medium text-sky-800"
                                >
                                  {topic}
                                </span>
                              ))}
                            </div>
                          )}
                          <p className="text-sm text-zinc-700 whitespace-pre-wrap">{item.guidance}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {studyAnalysis.study_plan?.length > 0 && (
                    <div className="rounded-lg border border-zinc-200 p-4">
                      <h3 className="font-bold text-zinc-900 mb-3">Próximos passos</h3>
                      <div className="space-y-2">
                        {studyAnalysis.study_plan.map((step, index) => (
                          <div key={`${step}-${index}`} className="flex gap-3 text-sm text-zinc-700">
                            <span className="w-6 h-6 rounded-full bg-zinc-900 text-white flex items-center justify-center text-xs flex-shrink-0">
                              {index + 1}
                            </span>
                            <span className="pt-0.5">{step}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {studyAnalysis.wrong_questions?.length > 0 && (
                    <div className="rounded-lg border border-zinc-200 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setShowWrongQuestions((value) => !value)}
                        className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-zinc-50 transition-colors"
                      >
                        <div>
                          <p className="font-bold text-zinc-900">Questões usadas nesta análise</p>
                          <p className="text-sm text-zinc-500">
                            Veja os enunciados considerados pelo Agente V.O.A.R.
                          </p>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform ${showWrongQuestions ? 'rotate-180' : ''}`} />
                      </button>

                      {showWrongQuestions && (
                        <div className="border-t border-zinc-200 divide-y divide-zinc-100">
                          {studyAnalysis.wrong_questions.map((question, index) => (
                            <div key={question.question_id} className="p-4">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                                <p className="text-sm font-semibold text-zinc-900">
                                  {index + 1}. {question.subject_name}
                                </p>
                                <p className="text-xs text-zinc-500">
                                  Errou {question.times_wrong} vez(es)
                                </p>
                              </div>
                              <p className="text-sm text-zinc-800 mb-3 whitespace-pre-wrap">
                                {question.question_text}
                              </p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                                <div className="rounded-md bg-red-50 border border-red-200 p-2 text-red-800">
                                  Marcou: {question.selected_option?.toUpperCase()} - {question.options?.[question.selected_option]}
                                </div>
                                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2 text-emerald-800">
                                  Resposta correta: {question.current_correct_option?.toUpperCase()} - {question.options?.[question.current_correct_option]}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default StatsPage;
